import { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';

type SvgIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>;

interface SidebarNavLinkProps {
  path: string;
  label: string;
  Icon?: SvgIcon;
  /** Shown after the label, e.g. progress; the collapsed rail shows a dot on the icon instead */
  badge?: ReactNode;
  /** Why the page isn't available yet; the link is then shown locked */
  lockedReason?: string;
}

// Only the collapsed rail hides the labels
const COLLAPSED_ONLY = 'hidden lg:block lg:sidebar-expanded:hidden 2xl:hidden';
const EXPANDED_ONLY = 'lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200';

export function SidebarNavLink({ path, label, Icon, badge, lockedReason }: SidebarNavLinkProps) {
  const { pathname } = useLocation();

  const active = !lockedReason && pathname.includes(path);

  const content = (
    <div className="flex items-center">
      {Icon && (
        <span className="relative shrink-0">
          <Icon
            className={cn(
              'stroke-current group-aria-[current=page]:text-primary',
              active ? 'text-violet-500 dark:text-violet-400' : 'text-gray-400 dark:text-gray-500',
            )}
            width={20}
            height={20}
            aria-hidden="true"
          />
          {badge && (
            <span className={cn('absolute -top-0.5 -right-0.5 size-2 rounded-full bg-violet-500', COLLAPSED_ONLY)} />
          )}
        </span>
      )}

      <span className={cn('text-sm font-medium ml-4 truncate', EXPANDED_ONLY)}>{label}</span>

      {badge && <span className={cn('ml-auto pl-2', EXPANDED_ONLY)}>{badge}</span>}
      {lockedReason && (
        <Lock className={cn('ml-auto shrink-0 text-gray-400', EXPANDED_ONLY)} size={16} aria-hidden="true" />
      )}
    </div>
  );

  const className = cn(
    'pl-4 pr-3 py-2 rounded-lg mb-0.5 last:mb-0 bg-[linear-gradient(135deg,var(--tw-gradient-stops))] block text-gray-800 dark:text-gray-100 truncate transition duration-150',
    active &&
      'hover:text-gray-900 dark:hover:text-white from-violet-500/[0.12] dark:from-violet-500/[0.24] to-violet-500/[0.04]',
    lockedReason && 'opacity-50 cursor-not-allowed',
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {lockedReason ? (
          <span role="link" aria-disabled="true" aria-label={`${label} – ${lockedReason}`} tabIndex={0} className={className}>
            {content}
          </span>
        ) : (
          <NavLink to={path} aria-label={label} className={className}>
            {content}
          </NavLink>
        )}
      </TooltipTrigger>
      {lockedReason ? (
        <TooltipContent side="right">
          <p className={cn('font-medium', COLLAPSED_ONLY)}>{label}</p>
          <p>{lockedReason}</p>
        </TooltipContent>
      ) : (
        <TooltipContent side="right" className={COLLAPSED_ONLY}>
          {label}
        </TooltipContent>
      )}
    </Tooltip>
  );
}
